import { Box, SxProps, Theme } from "@mui/material";
import React, {
  createContext,
  FC,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useRef,
  useEffect,
  useState,
} from "react";

interface IPosition {
  left: number;
  top: number;
}

interface IScrollPosition {
  prev: IPosition;
  curr: IPosition;
}

export interface IScrollValues {
  left: number;
  top: number;
  scrollLeft: number;
  scrollTop: number;
  scrollWidth: number;
  scrollHeight: number;
  clientWidth: number;
  clientHeight: number;
}

const initialPosition: IScrollPosition = {
  prev: { left: 0, top: 0 },
  curr: { left: 0, top: 0 },
};

export interface IScrollContextProps {
  isBottom: boolean;
  setHowMuchBelow: (howMuchBelow: number) => void;
  scrollTop: (top?: number) => void;
  scrolled: boolean;
  howMuchBelow?: number;
  scroll: RefObject<HTMLDivElement> | null;
  update(callback?: (values: IScrollValues) => void): void;
  moving: boolean;
}

export interface IScrollProps {
  howMuchBelow?: number;
  sx?: SxProps<Theme>;
}

export const ScrollContext = createContext<IScrollContextProps>({
  isBottom: false,
  scrolled: false,
  setHowMuchBelow: () => {},
  scrollTop: () => {},
  scroll: null,
  update: () => {},
  moving: false,
});

export const useScroll = ({ howMuchBelow }: IScrollProps = {}) => {
  const context = useContext(ScrollContext);
  if (howMuchBelow !== undefined && howMuchBelow != context.howMuchBelow) {
    context.setHowMuchBelow(howMuchBelow);
  }
  return context;
};

export const ProviderScroll: FC<PropsWithChildren<IScrollProps>> = ({
  children,
  howMuchBelow: initHowMuchBelow,
  sx,
}) => {
  const scroll = useRef<HTMLDivElement>(null);
  const [isBottom, setIsBottom] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [howMuchBelow, setHowMuchBelowState] = useState(initHowMuchBelow);
  const [moving, setMoving] = useState(false);
  const [position, setPosition] = useState<IScrollPosition>(initialPosition);

  const scrollTop = useCallback(
    (top = 0) => {
      if (scroll.current) {
        scroll.current.scrollTop = top;
      }
    },
    [scroll]
  );

  const setHowMuchBelow = useCallback(
    (value: number) => {
      if (value !== howMuchBelow) {
        setHowMuchBelowState(value);
      }
    },
    [howMuchBelow]
  );

  const handleScroll = useCallback(
    (currentTarget: HTMLDivElement) => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft } =
        currentTarget;
      const scrollThreshold = howMuchBelow ?? 0;

      const scrollableHeight = scrollHeight - clientHeight;
      const isAtBottom =
        scrollableHeight <= 0 ||
        scrollTop >= scrollableHeight - scrollThreshold;

      setIsBottom(isAtBottom);
      setScrolled(scrollTop > scrollThreshold);

      setPosition((prevPosition) => ({
        prev: prevPosition.curr,
        curr: {
          top: scrollTop,
          left: scrollLeft,
        },
      }));
    },
    [howMuchBelow]
  );

  const update = useCallback(
    (callback?: (values: IScrollValues) => void) => {
      if (scroll.current) {
        const {
          scrollTop,
          scrollHeight,
          clientHeight,
          scrollLeft,
          scrollWidth,
          clientWidth,
        } = scroll.current;

        const scrollableHeight = scrollHeight - clientHeight;
        const scrollableWidth = scrollWidth - clientWidth;

        const values: IScrollValues = {
          top: scrollableHeight > 0 ? scrollTop / scrollableHeight : 1,
          left: scrollableWidth > 0 ? scrollLeft / scrollableWidth : 1,
          scrollTop,
          scrollHeight,
          clientHeight,
          scrollLeft,
          scrollWidth,
          clientWidth,
        };

        handleScroll(scroll.current);

        callback?.(values);
      }
    },
    [handleScroll]
  );

  useEffect(() => {
    const isMovingDown = position.curr.top > position.prev.top;
    if (isMovingDown !== moving) {
      setMoving(isMovingDown);
    }
  }, [position.curr.top, position.prev.top, moving]);

  return (
    <ScrollContext.Provider
      value={{
        isBottom,
        moving,
        scroll,
        scrolled,
        scrollTop,
        setHowMuchBelow,
        update,
        howMuchBelow,
      }}
    >
      <Box
        ref={scroll}
        onScroll={(e) => handleScroll(e.currentTarget)}
        sx={sx || { overflow: "auto", height: "100%" }}
        className="scroll-container"
      >
        {children}
      </Box>
    </ScrollContext.Provider>
  );
};
